/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'

import type { EuiBasicTableColumn } from '@elastic/eui'
import { EuiFlexGroup, EuiLink, EuiSkeletonText, EuiText } from '@elastic/eui'

import type { CloudResource } from '@modules/billing-api/v1/types'
import { FormattedMessage } from '@modules/i18n'
import { addToast } from '@modules/cui/Toasts'
import useDeleteBudget from '@modules/billing-lib/mutations/useDeleteBudget'
import useUpdateBudget, {
  type UpdateBudgetBody,
} from '@modules/billing-lib/mutations/useUpdateBudget'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import Status from '../../components/Status'
import BalanceInfo from '../../components/BalanceInfo'
import useBillingNotificationsNav from '../../hooks/useBillingNotificationsNav'
import BadgeList from '../../components/BadgeList'
import { ActionType, BudgetsTableField } from '../types'

import { getAlertThresholds, getBalanceData } from './utils'
import ScopeItem from './ScopeItem'

import type { ActionError, Budget, Costs } from '../types'

const successMessages = {
  [ActionType.Activate]: {
    id: 'billing.notifications.budgets.table.actions.toggle.activate.success',
    defaultMessage: 'Budget "{budgetName}" active',
  },
  [ActionType.Deactivate]: {
    id: 'billing.notifications.budgets.table.actions.toggle.deactivate.success',
    defaultMessage: 'Budget "{budgetName}" inactive',
  },
  [ActionType.Delete]: {
    id: 'billing.notifications.budgets.table.actions.delete.success',
    defaultMessage: 'Budget "{budgetName}" deleted',
  },
}

type Props = {
  cloudResources: CloudResource[] | undefined
  costs: Costs | undefined
  onDeleteIntent: (budget: Budget) => void
  onViewDetails: (budget: Budget, costs?: Costs) => void
  onActionError: (error: ActionError) => void
  onCleanActionError: (budgetId: Budget['id']) => void
}

const useColumns = ({
  cloudResources,
  costs,
  onDeleteIntent,
  onViewDetails,
  onActionError,
  onCleanActionError,
}: Props): {
  columns: Array<EuiBasicTableColumn<Budget>>
  onDeleteConfirmed: (budget: Budget) => void
} => {
  const { goToEditBudget } = useBillingNotificationsNav()

  const organizationId = useOrganizationId()

  const updateBudgetMutation = useUpdateBudget()
  const deleteBudgetMutation = useDeleteBudget()

  const handleEditBudget = (budget: Budget) => goToEditBudget(budget)

  const handleToggleBudgetStatus = (budget: Budget) => {
    const { id: budgetId, name: budgetName } = budget

    if (!organizationId || !budgetId) {
      return
    }

    const isActive = !budget.active
    const actionType = isActive ? ActionType.Activate : ActionType.Deactivate

    const updatedBudget: UpdateBudgetBody = {
      active: isActive,
      alerts: budget.alerts.map(
        ({ operator, threshold, threshold_type }): UpdateBudgetBody['alerts'][number] => ({
          operator,
          threshold,
          threshold_type,
        }),
      ),
      amount: budget.amount,
      name: budget.name,
      period: budget.period,
      scope_type: budget.scope_type,
      scope_values: budget.scope_values,
    }

    updateBudgetMutation.mutate(
      {
        organizationId,
        budgetId: budgetId.toString(),
        body: updatedBudget,
      },
      {
        onSuccess: () => {
          onCleanActionError(budgetId)

          addToast({
            color: 'primary',
            title: <FormattedMessage {...successMessages[actionType]} values={{ budgetName }} />,
          })
        },
        onError: () => {
          const newTogglingError = {
            [budgetId]: {
              type: actionType,
              budgetName,
            },
          } as ActionError

          onActionError(newTogglingError)
        },
      },
    )
  }

  const handleDeleteBudget = (budget: Budget) => {
    const { id: budgetId, name: budgetName } = budget

    if (!organizationId || !budgetId) {
      return
    }

    deleteBudgetMutation.mutate(
      {
        organizationId,
        budgetId,
      },
      {
        onSuccess: () => {
          onCleanActionError(budgetId)

          addToast({
            color: 'primary',
            title: (
              <FormattedMessage {...successMessages[ActionType.Delete]} values={{ budgetName }} />
            ),
          })
        },
        onError: () => {
          const newDeletionError = {
            [budgetId]: {
              type: ActionType.Delete,
              budgetName,
            },
          } as ActionError

          onActionError(newDeletionError)
        },
      },
    )
  }

  const hasActionsDisabled = updateBudgetMutation.isLoading || deleteBudgetMutation.isLoading

  const columns = [
    {
      field: BudgetsTableField.NAME,
      name: (
        <FormattedMessage
          id='billing.notifications.budgets.table.name-header'
          defaultMessage='Name'
        />
      ),
      width: '25%',
      truncateText: true,
      mobileOptions: { width: '100%' },
    },
    {
      field: BudgetsTableField.ACTIVE,
      name: (
        <FormattedMessage
          id='billing.notifications.budgets.table.active-header'
          defaultMessage='Status'
        />
      ),
      render: (active: Budget['active']) => <Status active={active} />,
      width: '100px',
      mobileOptions: { width: '100%' },
    },
    {
      field: BudgetsTableField.SCOPE,
      name: (
        <FormattedMessage
          id='billing.notifications.budgets.table.scope-header'
          defaultMessage='Scope'
        />
      ),
      render: (_: unknown, budget: Budget) => (
        <ScopeItem color='hollow' scopeType={budget.scope_type} scopeValues={budget.scope_values} />
      ),
      width: '256px',
      mobileOptions: { width: '100%' },
    },
    {
      field: BudgetsTableField.BALANCE,
      name: (
        <FormattedMessage
          id='billing.notifications.budgets.table.usage-header'
          defaultMessage='Current vs Budgeted (ECU)'
        />
      ),
      render: (_: unknown, budget: Budget) => {
        const isCloudResource = budget.scope_type === 'cloud_resource'

        if (!costs || (isCloudResource && !cloudResources)) {
          return (
            // we need this wrapper otherwise loading state doesn't take full width of the cell
            <div css={css({ width: '100%' })}>
              <EuiSkeletonText lines={1} />
            </div>
          )
        }

        return (
          <BalanceInfo
            {...getBalanceData({ budget, costs, cloudResources })}
            showPercentage={true}
          />
        )
      },
      width: '270px',
      mobileOptions: { width: '100%' },
    },

    {
      field: BudgetsTableField.ALERTS,
      name: (
        <FormattedMessage
          id='billing.notifications.budgets.table.alerts-header'
          defaultMessage='Notify at'
        />
      ),
      readOnly: true,
      truncateText: true,
      render: (alerts: Budget['alerts']) => (
        <EuiFlexGroup direction='column' gutterSize='xs'>
          <EuiFlexGroup gutterSize='xs' responsive={false} alignItems='center' wrap={true}>
            <BadgeList items={getAlertThresholds(alerts)} color='hollow' />
          </EuiFlexGroup>
          <EuiText size='xs'>
            <FormattedMessage
              id='billing.notifications.budgets.table.thresholds-description'
              defaultMessage='of budget amount'
            />
          </EuiText>
        </EuiFlexGroup>
      ),
      width: '140px',
      mobileOptions: { width: '100%' },
    },
    {
      name: (
        <FormattedMessage
          id='billing.notifications.budgets.table.actions-header'
          defaultMessage='Actions'
        />
      ),
      render: () => null,
      width: '140px',
      align: 'right',
      readOnly: true,
      actions: [
        {
          isPrimary: true,
          render: (budget: Budget) => (
            <EuiLink
              color='primary'
              data-test-subj='billing.notifications.budgets.table.actions.view'
              disabled={hasActionsDisabled}
              onClick={() => onViewDetails(budget, costs)}
              type='button'
            >
              <FormattedMessage
                id='billing.notifications.budgets.table.actions.view'
                defaultMessage='View'
              />
            </EuiLink>
          ),
        },
        {
          isPrimary: true,
          render: (budget: Budget) => (
            <EuiLink
              color='primary'
              data-test-subj='billing.notifications.budgets.table.actions.edit'
              disabled={hasActionsDisabled}
              onClick={() => handleEditBudget(budget)}
              type='button'
            >
              <FormattedMessage
                id='billing.notifications.budgets.table.actions.edit'
                defaultMessage='Edit'
              />
            </EuiLink>
          ),
        },
        {
          showOnHover: false,
          render: (budget: Budget) => (
            <EuiLink
              color='primary'
              data-test-subj={`billing.notifications.budgets.table.actions.toggle.${budget.active ? 'deactivate' : 'activate'}`}
              disabled={hasActionsDisabled}
              onClick={() => handleToggleBudgetStatus(budget)}
              type='button'
            >
              {budget.active ? (
                <FormattedMessage
                  id='billing.notifications.budgets.table.actions.toggle.button.deactivate'
                  defaultMessage='Deactivate'
                />
              ) : (
                <FormattedMessage
                  id='billing.notifications.budgets.table.actions.toggle.button.activate'
                  defaultMessage='Activate'
                />
              )}
            </EuiLink>
          ),
        },
        {
          showOnHover: false,
          render: (budget: Budget) => (
            <EuiLink
              color='danger'
              data-test-subj='billing.notifications.budgets.table.actions.delete'
              disabled={hasActionsDisabled}
              onClick={() => onDeleteIntent(budget)}
              type='button'
            >
              <FormattedMessage
                id='billing.notifications.budgets.table.actions.delete'
                defaultMessage='Delete'
              />
            </EuiLink>
          ),
        },
      ],
      mobileOptions: { width: '100%' },
    },
  ]

  return {
    columns,
    onDeleteConfirmed: handleDeleteBudget,
  }
}

export default useColumns
